import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { logoutUser, setUserDetails, setCoinsCount, setJwtToken, setTutorialCompleted } from "../slices";
import { tokenStorage } from "../../utils/tokenStorage";
import { tokenRefreshService } from "../../services/tokenRefreshService";
import { broadcastTokenUpdate } from "../../utils/tokenBroadcast";
import { getBroadcastExpiresInSeconds, parseExpiresInParam } from "../../utils/authExpiry";
import { getUserEndpoint } from "../../utils/apiEndpoint";

const userEndpoint = getUserEndpoint();

function persistAuthSession(data, dispatch) {
  const { accessToken, expiresIn, token } = data;
  const access = accessToken || token;
  if (!access) return;
  const ttl = parseExpiresInParam(expiresIn);
  tokenStorage.saveAccessToken(access, ttl);
  dispatch(setJwtToken({ token: access }));
  broadcastTokenUpdate(
    access,
    getBroadcastExpiresInSeconds(access, ttl),
  );
}

const baseQueryWithAuth = async (args, api, extraOptions) => {
  const baseQuery = fetchBaseQuery({
    baseUrl: userEndpoint,
    prepareHeaders: async (headers) => {
      const accessToken = await tokenRefreshService.getValidAccessToken();
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
      return headers;
    },
  });

  const result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    api.dispatch(logoutUser());
  }

  return result;
};

export const userAuthApis = createApi({
    reducerPath: "userAuthApis",
    baseQuery: baseQueryWithAuth,
    refetchOnFocus: true,
    tagTypes: ["UserDetails"],
    endpoints: (builder) => ({
        authAttempt: builder.mutation({
            query: (newPost) => ({
                url: "auth-attempt",
                method: "POST",
                body: newPost,
            }),
        }),
        loginUser: builder.mutation({
            query: (newPost) => ({
                url: "signin",
                method: "POST",
                body: newPost.body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const result = await queryFulfilled;
                    if (result?.data?.data) {
                        persistAuthSession(result.data.data, dispatch);
                    }
                } catch (error) {
                    console.error('Login error:', error);
                }
            },
        }),
        loginUserOTP: builder.mutation({
            query: (newPost) => ({
                url: `auth-verify?platform=${newPost?.domain}`,
                method: "POST",
                body: newPost.body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const result = await queryFulfilled;
                    if (result?.data?.data) {
                        persistAuthSession(result.data.data, dispatch);
                    }
                } catch (error) {
                    console.error('OTP verification error:', error);
                }
            },
        }),
        signupUser: builder.mutation({
            query: (newPost) => ({
                url: "signup",
                method: "POST",
                body: newPost.body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const result = await queryFulfilled;
                    if (result?.data?.data) {
                        persistAuthSession(result.data.data, dispatch);
                    }
                } catch (error) {
                    console.error('Signup error:', error);
                }
            },
        }),
        logout: builder.mutation({
            query: () => ({
                url: "auth/logout",
                method: "POST",
                body: {},
            }),
        }),
        updateUser: builder.mutation({
            query: (newPost) => ({
                url: "update-user",
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                },
                body: newPost.body,
            }),
        }),
        getUserDetails: builder.query({
            query: () => {
                console.log("[Tutorial Debug] userLogin: GET /fetch-user request initiated", { baseUrl: userEndpoint });
                return {
                    url: `fetch-user`,
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                };
            },
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const result = await queryFulfilled;
                    console.log("[Tutorial Debug] userLogin: fetch-user success", {
                        hasCompletedTutorial: result?.data?.data?.hasCompletedTutorial,
                        userDetailsKeys: result?.data?.data ? Object.keys(result.data.data) : [],
                    });
                    if (result?.data?.data) {
                        dispatch(setUserDetails(result.data.data));
                        dispatch(setCoinsCount({ coinsCount: result.data.data?.totalScore }));
                    }
                } catch (error) {
                    console.log("[Tutorial Debug] userLogin: fetch-user error", {
                        status: error?.error?.status,
                        message: error?.error?.data?.message || error?.message,
                    });
                    if (error?.error?.status === 401) {
                        dispatch(logoutUser());
                    }
                }
            },
            providesTags: ["UserDetails"],
        }),
        completeTutorial: builder.mutation({
            query: () => ({
                url: "user/tutorial/complete",
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(setTutorialCompleted(true));
                } catch (error) {
                    if (error?.error?.status === 401) {
                        dispatch(logoutUser());
                    }
                }
            },
        }),
        resetTutorial: builder.mutation({
            query: () => ({
                url: "user/tutorial/reset",
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(setTutorialCompleted(false));
                } catch (error) {
                    if (error?.error?.status === 401) {
                        dispatch(logoutUser());
                    }
                }
            },
        }),
    }),

});

export const {
    useLoginUserMutation,
    useLazyGetUserDetailsQuery,
    useSignupUserMutation,
    useAuthAttemptMutation,
    useLoginUserOTPMutation,
    useUpdateUserMutation,
    useLogoutMutation,
    useCompleteTutorialMutation,
    useResetTutorialMutation,
} = userAuthApis;